import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

/**
 * Phase 168 — generic "enter a mandatory reason" dialog used for overrides, Sunday working days, leave decisions…
 * props: open, onOpenChange, title, description, confirmLabel, destructive, onConfirm(reason) (async), children (extra fields),
 *        optional (reason not required), testId
 */
export function ReasonDialog({ open, onOpenChange, title, description, confirmLabel = "Confirm", destructive = false, onConfirm,
  children, optional = false, placeholder = "Reason (required — kept in the audit log)", testId = "reason-dialog", icon: Icon }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setBusy(false); } }, [open]);
  const ok = optional || reason.trim().length >= 3;
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!ok || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <form onSubmit={submit} className="space-y-3">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">{Icon && <Icon className="h-5 w-5 text-primary" aria-hidden="true" />} {title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          {children}
          <div className="space-y-1">
            <Label className="text-xs">{optional ? "Reason / note (optional)" : "Reason (required)"}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={placeholder} maxLength={500}
              data-testid={`${testId}-reason`} aria-invalid={!optional && reason.length > 0 && reason.trim().length < 3} />
            {!optional && <p className="text-[11px] text-muted-foreground">At least 3 characters. The reason, your name and the time are logged for audit.</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
            <Button type="submit" variant={destructive ? "destructive" : "default"} disabled={!ok || busy} data-testid={`${testId}-confirm`}>
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
